import { Injectable } from '@angular/core';
import { Product } from "../shop/shop.product.model";
import { Bundle } from "../shop/shop.bundle.model";
import { ShopCartService } from "../shop/shop.cart.service";

export interface BundleProduct {
    id: number;
    count: number;
}

declare let __twingle_products: any;

@Injectable({
    providedIn: 'root'
})
export class BundlesService {
    /**
     * products
     */
    items: Array<Product | Bundle> = [];

    constructor(private cartService: ShopCartService) {
        this.loadProducts();
    }

    /**
     * load the products
     */
    private loadProducts(): void {
        if (typeof __twingle_products !== "undefined" && __twingle_products.length > 0) {
            this.items = __twingle_products.map(data => {
                if (data.type === 'bundle') {
                    const bundle = new Bundle();
                    Object.assign(bundle, data);
                    return bundle;
                }
                if (data.type === 'product') {
                    const product = new Product();
                    Object.assign(product, data);
                    return product;
                }
            }).filter((item: Product | Bundle) => {
                // check if product is available for purchase 
                // if it is a bundle, it is purchasable if all products in the bundle are purchasable
                const isPurchasable = item instanceof Product ? item.checkIfPurchasable() : true;

                // Check if the product does not have any digitial downloads
                const hasDigitalDownloads = (item instanceof Product) && item.file !== null;

                // return the product if any of the above conditions are false
                return isPurchasable || hasDigitalDownloads;
            });
        }
    }

    /**
     * adds the products in a bundle to the cart
     * products are added with the count of the bundle
     * products from a bundle can't have a custom text nor a custom price 
     * @param bundleProducts 
     */
    public addBundleToCart(bundleProducts: Array<BundleProduct>, bundleCount: number): void {
        bundleProducts.forEach((bundleProduct: BundleProduct) => {
            const product = this.items.find((item) => item.id === bundleProduct.id) as Product;

            if (product) {
                this.cartService.addItem(product, bundleProduct.count * bundleCount, true, product.price === null ? 0 : null, null, product.has_force_count);
            }
        });
    }

    /**
     * we need to prepare the bundle information from the individual products once when the products are loaded
     * @param bundle 
     */
    public prepareBundleInformation(bundle: Bundle): void {
        if (bundle.type !== 'bundle') return;

        let bundlePrice = this.getBundlePrice(bundle);

        // set price and max_count
        bundle.price = bundlePrice;

        // map the products in the bundle
        bundle.bundle_products = bundle.bundle_products.map((bundleProduct: BundleProduct) => {
            const product = this.items.find((item) => item.id === bundleProduct.id) as Product;

            if (product) {
                bundle.categories = [...bundle.categories, ...product.categories].filter((value, index, self) => self.indexOf(value) === index);

                // set the bundle as not purchasable if any of the products in the bundle is not purchasable
                if (bundle.isPurchasable && !product.isPurchasable) { 
                    bundle.isPurchasable = false;
                }

                return {
                    id: product.id,
                    count: bundleProduct.count,
                    product: product
                };
            } else {
                // if the product is not found, set the bundle as not purchasable
                bundle.availableCount = 0;
                bundle.isPurchasable = false;
            }
        });
    }

    /**
     * Returns the total price of a bundle, based on the products in the bundle
     * @param bundle
     * @returns price
     */
    public getBundlePrice(bundle: Bundle): number {
        if (bundle.type !== 'bundle') return;

        let bundlePrice = 0;

        bundle.bundle_products.forEach((bundleProduct: BundleProduct) => {
            const product = this.items.find((product: Product) => product.id === bundleProduct.id);

            if (product) {
                bundlePrice += product.price * bundleProduct.count;
            }  else {
                // if a product is not found, set the bundle as not purchasable
                bundle.availableCount = 0;
                bundle.isPurchasable = false;
            }
        });

        return bundlePrice;
    }

    /**
     * Returns the available count for a bundle, based on the products in the bundle
     * @param bundle 
     * @returns availableCount
     */
    public getAvailableCountForBundle(bundle: Bundle): number {
        if (bundle.type !== 'bundle') return;
        let availableCount = Infinity;

        bundle.bundle_products.forEach((bundleProduct: BundleProduct) => {
            const product = this.items.find((item) => item.id === bundleProduct.id) as Product;

            if (product) {
                // total available count
                const availableProductCount = product.getMaxCount();

                // product count in cart
                const productCountInCart = this.cartService.items.filter(item => item.product.id === product.id).reduce((count, item) => count + item.count, 0);

                // available count for the bundle
                const availableCountInBundle = Math.floor((availableProductCount - productCountInCart) / bundleProduct.count);

                // make sure that the available count is the lowest available count of all products in the bundle
                availableCount = Math.min(availableCount, availableCountInBundle);
            } else {
                // if the product is not found, set the bundle as not purchasable
                bundle.availableCount = 0;
                bundle.isPurchasable = false;
            }
        });

        // set the bundle available count
        bundle.availableCount = availableCount;

        return availableCount;
    }
}
